// Parses OpenFoodFacts' free-text `quantity` field (e.g. "24 bars",
// "6 x 330 ml", "500 g", "1 L") into a discrete pack count + unit when the
// label expresses a *countable* pack. That lets the Add Item form prefill
// quantity=24, unit="bars" instead of quantity=1, unit="24 bars" — so the −1
// button decrements one bar at a time, matching how people think about a box of
// 24. Pure measurements ("500 g", "1 L") return `quantity: null` and keep the
// original text as the unit; quantity stays at the form default of 1.
//
// Kept dependency-free so both the worker (functions/api/[[path]].ts) and the
// browser-direct lookup (src/api.ts) can import it.

export interface PackSize {
  // Number of discrete units in the pack, if the label expresses one (always > 1).
  quantity: number | null;
  // Unit to show: the count word ("bars"), the sub-pack descriptor ("× 330 ml"),
  // or — for a bare measurement — the original text ("500 g"). null if input was empty.
  unit: string | null;
}

// Tokens that mean "this is a measurement, not a count of items". Anything
// matching these after an integer (e.g. the "g" in "500 g") is left alone.
const MEASURE_UNIT = /^(?:m?[glL]|kg|cl|dl|cc|ml|oz|m?oz|lb|lbs|fl\s?oz|kj|kcal|%)$/i;

export function parsePackSize(raw: string | null | undefined): PackSize {
  const text = (raw ?? "").trim();
  if (!text) return { quantity: null, unit: null };

  // "6 x 330 ml" / "4 × 125 g" / "2x1L" — N of a sub-pack.
  const multi = text.match(/^(\d+)\s*[x×*]\s*(.+)$/i);
  if (multi) {
    const n = parseInt(multi[1], 10);
    if (Number.isFinite(n) && n > 1) {
      return { quantity: n, unit: `× ${multi[2].trim()}` };
    }
  }

  // "24 bars" / "12 ct" / "10 sachets" / "6 pack" — integer followed by a
  // word that isn't a measurement unit. Trailing period tolerated ("12 ct.").
  const counted = text.match(/^(\d+)\s*([\p{L}.]+?)\.?$/u);
  if (counted) {
    const n = parseInt(counted[1], 10);
    const word = counted[2];
    if (Number.isFinite(n) && n > 1 && !MEASURE_UNIT.test(word)) {
      return { quantity: n, unit: word };
    }
  }

  // Bare measurement, single-unit pack, or anything we don't recognise:
  // keep the text as the unit, leave quantity unset.
  return { quantity: null, unit: text };
}
